var { initIframePrint } = require('./shared/iframe-print');

/**
 * Habit Tracker - Track daily habits with streaks and progress monitoring
 * @module HabitTracker
 */
(function() {
    'use strict';

    const STORAGE_KEY = 'habit-tracker-data';
    let habits = [];
    let currentWeekStart = getWeekStart(new Date());

    /**
     * Get the Monday of a given week
     * @param {Date} date - The date to get the week start for
     * @returns {Date} The Monday of that week
     */
    function getWeekStart(date) {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    }

    /**
     * Format week display string
     * @param {Date} weekStart - The start of the week
     * @returns {string} Formatted week range
     */
    function formatWeekDisplay(weekStart) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        
        const options = { month: 'short', day: 'numeric' };
        const startStr = weekStart.toLocaleDateString('en-US', options);
        const endStr = weekEnd.toLocaleDateString('en-US', options);
        const year = weekEnd.getFullYear();
        
        return `${startStr} - ${endStr}, ${year}`;
    }

    /**
     * Get ISO date key from Date object
     * @param {Date} date - The date to convert
     * @returns {string} ISO date string (YYYY-MM-DD)
     */
    function getDateKey(date) {
        return date.toISOString().split('T')[0];
    }

    /**
     * Get array of date keys for the current week
     * @returns {string[]} Array of 7 date keys
     */
    function getWeekDates() {
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(currentWeekStart);
            date.setDate(date.getDate() + i);
            dates.push(getDateKey(date));
        }
        return dates;
    }

    /**
     * Load habits data from localStorage
     */
    function loadData() {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                habits = JSON.parse(saved);
            } catch (e) {
                habits = [];
            }
        }
    }

    /**
     * Save habits data to localStorage
     */
    function saveData() {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(habits));
    }

    /**
     * Calculate current streak for a habit
     * @param {Object} habit - The habit object
     * @returns {number} The current streak count
     */
    function calculateStreak(habit) {
        const today = getDateKey(new Date());
        let streak = 0;
        let currentDate = new Date();
        
        if (!habit.completedDates.includes(today)) {
            currentDate.setDate(currentDate.getDate() - 1);
        }
        
        while (true) {
            const dateKey = getDateKey(currentDate);
            if (habit.completedDates.includes(dateKey)) {
                streak++;
                currentDate.setDate(currentDate.getDate() - 1);
            } else {
                break;
            }
        }
        
        return streak;
    }

    /**
     * Escape HTML to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string} Escaped HTML
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Render all habits to the table
     */
    function renderHabits() {
        const tbody = document.getElementById('habits-tbody');
        const emptyState = document.getElementById('empty-state');
        const statsSection = document.getElementById('stats-section');
        const weekDates = getWeekDates();
        const today = getDateKey(new Date());

        tbody.innerHTML = '';

        if (habits.length === 0) {
            emptyState.classList.remove('d-none');
            statsSection.classList.add('d-none');
            return;
        }

        emptyState.classList.add('d-none');
        statsSection.classList.remove('d-none');

        habits.forEach((habit, index) => {
            const streak = calculateStreak(habit);
            const row = document.createElement('tr');
            
            let cells = `<td class="habit-name-col">
                <span class="habit-name">${escapeHtml(habit.name)}</span>
            </td>`;
            
            weekDates.forEach((dateKey, dayIndex) => {
                const isCompleted = habit.completedDates.includes(dateKey);
                const isToday = dateKey === today;
                const isPast = dateKey < today;
                const isFuture = dateKey > today;
                const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                const ariaLabel = `${escapeHtml(habit.name)} - ${dayNames[dayIndex]}${isCompleted ? ' (completed)' : ''}`;
                
                cells += `<td class="day-col ${isToday ? 'today' : ''} ${isPast && !isCompleted ? 'missed' : ''}">
                    <label class="habit-check ${isFuture ? 'future' : ''}">
                        <input type="checkbox" 
                               data-habit="${index}" 
                               data-date="${dateKey}"
                               aria-label="${ariaLabel}"
                               ${isCompleted ? 'checked' : ''}
                               ${isFuture ? 'disabled' : ''} />
                        <span class="checkmark" aria-hidden="true">${isCompleted ? '<i class="bi bi-check-lg"></i>' : ''}</span>
                    </label>
                </td>`;
            });
            
            cells += `<td class="streak-col">
                <span class="streak-badge ${streak > 0 ? 'active' : ''}">${streak}</span>
            </td>`;
            
            cells += `<td class="actions-col">
                <button type="button" class="btn-delete-habit" data-index="${index}" aria-label="Delete ${escapeHtml(habit.name)}">
                    <i class="bi bi-trash" aria-hidden="true"></i>
                </button>
            </td>`;
            
            row.innerHTML = cells;
            tbody.appendChild(row);
        });

        attachEventListeners();
        updateStats();
    }

     /**
      * Attach event listeners to dynamically created elements
      */
     function attachEventListeners() {
         const tbody = document.getElementById('habits-tbody');
         
         tbody.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
             checkbox.addEventListener('change', (e) => {
                 const habitIndex = parseInt(e.target.dataset.habit);
                 const dateKey = e.target.dataset.date;
                 
                 if (e.target.checked) {
                     if (!habits[habitIndex].completedDates.includes(dateKey)) {
                         habits[habitIndex].completedDates.push(dateKey);
                     }
                 } else {
                     habits[habitIndex].completedDates = habits[habitIndex].completedDates.filter(d => d !== dateKey);
                 }
                 
                 saveData();
                 renderHabits();
             });
         });

         tbody.querySelectorAll('.btn-delete-habit').forEach(btn => {
             btn.addEventListener('click', () => {
                 const index = parseInt(btn.dataset.index);
                 deleteHabit(index);
             });
         });
     }

     /**
      * Delete a habit immediately without confirmation
      * Matches WeeklyTodoList pattern for consistency
      * @param {number} habitIndex - The index of the habit to delete
      */
     function deleteHabit(habitIndex) {
         habits.splice(habitIndex, 1);
         saveData();
         renderHabits();
     }

     /**
      * Initialize clear all confirmation modal controls
      */
     function initClearConfirmation() {
         const clearButton = document.getElementById('btn-clear-all');
         const overlay = document.getElementById('clearConfirmationOverlay');
         if (!clearButton || !overlay) return;

         const closeButtons = overlay.querySelectorAll('[data-clear-confirmation="close"]');
         const confirmBtn = overlay.querySelector('[data-clear-confirmation="confirm"]');

         clearButton.addEventListener('click', function () {
             openClearConfirmation(overlay);
         });

         closeButtons.forEach(function (btn) {
             btn.addEventListener('click', function () {
                 closeClearConfirmation(overlay);
             });
         });

         if (confirmBtn) {
             confirmBtn.addEventListener('click', function () {
                 closeClearConfirmation(overlay);
                 clearAllHabits();
                 window.scrollTo({ top: 0, behavior: 'smooth' });
             });
         }
     }

     /**
      * Open the clear confirmation modal
      * @param {HTMLElement} overlay - The modal overlay element
      */
     function openClearConfirmation(overlay) {
         const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
         if (scrollbarWidth > 0) {
             document.body.style.paddingRight = scrollbarWidth + 'px';
         }
         overlay.classList.add('is-visible');
         overlay.setAttribute('aria-hidden', 'false');
         document.body.classList.add('clear-confirmation-open');
     }

     /**
      * Close the clear confirmation modal
      * @param {HTMLElement} overlay - The modal overlay element
      */
     function closeClearConfirmation(overlay) {
         overlay.classList.remove('is-visible');
         overlay.setAttribute('aria-hidden', 'true');
         document.body.classList.remove('clear-confirmation-open');
         document.body.style.paddingRight = '';
     }

     /**
      * Clear all habits
      */
     function clearAllHabits() {
         habits = [];
         saveData();
         renderHabits();
     }

    /**
     * Update statistics display
     */
    function updateStats() {
        const today = getDateKey(new Date());
        const weekDates = getWeekDates();
        
        const totalHabits = habits.length;
        const completedToday = habits.filter(h => h.completedDates.includes(today)).length;
        
        let weekTotal = 0;
        let weekCompleted = 0;
        const todayIndex = weekDates.indexOf(today);
        const daysToCount = todayIndex >= 0 ? todayIndex + 1 : 7;
        
        habits.forEach(habit => {
            for (let i = 0; i < daysToCount; i++) {
                weekTotal++;
                if (habit.completedDates.includes(weekDates[i])) {
                    weekCompleted++;
                }
            }
        });
        
        const weekCompletion = weekTotal > 0 ? Math.round((weekCompleted / weekTotal) * 100) : 0;
        const longestStreak = habits.reduce((max, h) => Math.max(max, calculateStreak(h)), 0);

        document.getElementById('stat-total-habits').textContent = totalHabits;
        document.getElementById('stat-completed-today').textContent = completedToday;
        document.getElementById('stat-week-completion').textContent = weekCompletion + '%';
        document.getElementById('stat-longest-streak').textContent = longestStreak;
    }

    /**
     * Update week display text and day headers with proper timezone handling
     */
    function updateWeekDisplay() {
        document.getElementById('week-display').textContent = formatWeekDisplay(currentWeekStart);
        document.getElementById('print-week').textContent = formatWeekDisplay(currentWeekStart);
        
        const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const headers = document.querySelectorAll('.habits-table thead .day-col');
        
        headers.forEach((header, i) => {
            const date = new Date(currentWeekStart);
            date.setDate(date.getDate() + i);
            const dayNum = date.getDate();
            header.innerHTML = `${dayNames[i]}<br><small>${dayNum}</small>`;
        });
    }

    /**
     * Add a new habit
     * @param {string} name - The habit name
     */
    function addHabit(name) {
        if (!name.trim()) return;
        
        habits.push({
            name: name.trim(),
            completedDates: [],
            createdAt: new Date().toISOString()
        });
        
        saveData();
        renderHabits();
    }

     /**
      * Initialize the application
      */
     function init() {
         initIframePrint({
             formId: 'htPrintForm',
             iframeId: 'htPrintFrame',
             buttonId: 'htPrintBtn',
             readyLabel: '<i class="bi bi-printer me-2" aria-hidden="true"></i>Print Tracker',
         });

         loadData();
         updateWeekDisplay();
         renderHabits();

         document.getElementById('btn-add-habit').addEventListener('click', () => {
             const input = document.getElementById('new-habit');
             addHabit(input.value);
             input.value = '';
             input.focus();
         });

         document.getElementById('new-habit').addEventListener('keypress', (e) => {
             if (e.key === 'Enter') {
                 document.getElementById('btn-add-habit').click();
             }
         });

         document.querySelectorAll('.suggested-btn').forEach(btn => {
             btn.addEventListener('click', () => {
                 addHabit(btn.dataset.habit);
             });
         });

         document.getElementById('btn-prev-week').addEventListener('click', () => {
             currentWeekStart.setDate(currentWeekStart.getDate() - 7);
             updateWeekDisplay();
             renderHabits();
         });

         document.getElementById('btn-next-week').addEventListener('click', () => {
             currentWeekStart.setDate(currentWeekStart.getDate() + 7);
             updateWeekDisplay();
             renderHabits();
         });

         initClearConfirmation();
     }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
